package songwritersnotebook.database

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Long
import kotlin.String

public class SongQueriesQueries(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> getAllSongs(mapper: (
    id: String,
    ownerId: String,
    title: String,
    lastModified: Long,
    bpm: Long,
    isDeleted: Long,
    data_: String,
  ) -> T): Query<T> = Query(-1_487_051_701, arrayOf("SongEntity"), driver, "SongQueries.sq",
      "getAllSongs", """
  |SELECT * FROM SongEntity 
  |ORDER BY lastModified DESC
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getString(6)!!
    )
  }

  public fun getAllSongs(): Query<SongEntity> = getAllSongs { id, ownerId, title, lastModified, bpm,
      isDeleted, data_ ->
    SongEntity(
      id,
      ownerId,
      title,
      lastModified,
      bpm,
      isDeleted,
      data_
    )
  }

  public fun <T : Any> getAllSongsInternal(ownerId: String, mapper: (
    id: String,
    ownerId: String,
    title: String,
    lastModified: Long,
    bpm: Long,
    isDeleted: Long,
    data_: String,
  ) -> T): Query<T> = GetAllSongsInternalQuery(ownerId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getString(6)!!
    )
  }

  public fun getAllSongsInternal(ownerId: String): Query<SongEntity> =
      getAllSongsInternal(ownerId) { id, ownerId_, title, lastModified, bpm, isDeleted, data_ ->
    SongEntity(
      id,
      ownerId_,
      title,
      lastModified,
      bpm,
      isDeleted,
      data_
    )
  }

  public fun <T : Any> getSongById(
    id: String,
    ownerId: String,
    mapper: (
      id: String,
      ownerId: String,
      title: String,
      lastModified: Long,
      bpm: Long,
      isDeleted: Long,
      data_: String,
    ) -> T,
  ): Query<T> = GetSongByIdQuery(id, ownerId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getString(6)!!
    )
  }

  public fun getSongById(id: String, ownerId: String): Query<SongEntity> = getSongById(id,
      ownerId) { id_, ownerId_, title, lastModified, bpm, isDeleted, data_ ->
    SongEntity(
      id_,
      ownerId_,
      title,
      lastModified,
      bpm,
      isDeleted,
      data_
    )
  }

  public fun <T : Any> getVersionsForSong(
    songId: String,
    ownerId: String,
    mapper: (
      id: String,
      ownerId: String,
      songId: String,
      timestamp: Long,
      versionNumber: Long,
      type: String,
      notes: String,
      data_: String,
    ) -> T,
  ): Query<T> = GetVersionsForSongQuery(songId, ownerId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7)!!
    )
  }

  public fun getVersionsForSong(songId: String, ownerId: String): Query<SongVersionEntity> =
      getVersionsForSong(songId, ownerId) { id, ownerId_, songId_, timestamp, versionNumber, type,
      notes, data_ ->
    SongVersionEntity(
      id,
      ownerId_,
      songId_,
      timestamp,
      versionNumber,
      type,
      notes,
      data_
    )
  }

  public fun <T : Any> getHarmonyChatHistory(
    songId: String,
    userId: String,
    `value`: Long,
    mapper: (
      id: String,
      songId: String,
      userId: String,
      timestamp: Long,
      text: String,
      isUser: Long,
    ) -> T,
  ): Query<T> = GetHarmonyChatHistoryQuery(songId, userId, value) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getLong(3)!!,
      cursor.getString(4)!!,
      cursor.getLong(5)!!
    )
  }

  public fun getHarmonyChatHistory(
    songId: String,
    userId: String,
    value_: Long,
  ): Query<HarmonyChatMessageEntity> = getHarmonyChatHistory(songId, userId, value_) { id, songId_,
      userId_, timestamp, text, isUser ->
    HarmonyChatMessageEntity(
      id,
      songId_,
      userId_,
      timestamp,
      text,
      isUser
    )
  }

  public suspend fun insertSong(
    id: String,
    ownerId: String,
    title: String,
    lastModified: Long,
    bpm: Long,
    isDeleted: Long,
    data_: String,
  ) {
    driver.execute(-1_977_449_866, """
        |INSERT OR REPLACE INTO SongEntity(id, ownerId, title, lastModified, bpm, isDeleted, data)
        |VALUES (?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 7) {
          bindString(0, id)
          bindString(1, ownerId)
          bindString(2, title)
          bindLong(3, lastModified)
          bindLong(4, bpm)
          bindLong(5, isDeleted)
          bindString(6, data_)
        }.await()
    notifyQueries(-1_977_449_866) { emit ->
      emit("SongEntity")
    }
  }

  public suspend fun deleteSong(id: String) {
    driver.execute(-1_179_026_328, """DELETE FROM SongEntity WHERE id = ?""", 1) {
          bindString(0, id)
        }.await()
    notifyQueries(-1_179_026_328) { emit ->
      emit("HarmonyChatMessageEntity")
      emit("SongEntity")
      emit("SongVersionEntity")
    }
  }

  public suspend fun insertVersion(
    id: String,
    ownerId: String,
    songId: String,
    timestamp: Long,
    versionNumber: Long,
    type: String,
    notes: String,
    data_: String,
  ) {
    driver.execute(1_942_849_495, """
        |INSERT OR REPLACE INTO SongVersionEntity(id, ownerId, songId, timestamp, versionNumber, type, notes, data)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 8) {
          bindString(0, id)
          bindString(1, ownerId)
          bindString(2, songId)
          bindLong(3, timestamp)
          bindLong(4, versionNumber)
          bindString(5, type)
          bindString(6, notes)
          bindString(7, data_)
        }.await()
    notifyQueries(1_942_849_495) { emit ->
      emit("SongVersionEntity")
    }
  }

  public suspend fun deleteVersion(id: String, ownerId: String) {
    driver.execute(-2_045_382_491, """DELETE FROM SongVersionEntity WHERE id = ? AND ownerId = ?""",
        2) {
          bindString(0, id)
          bindString(1, ownerId)
        }.await()
    notifyQueries(-2_045_382_491) { emit ->
      emit("SongVersionEntity")
    }
  }

  public suspend fun updateVersionNotes(
    notes: String,
    id: String,
    ownerId: String,
  ) {
    driver.execute(-1_188_101_318,
        """UPDATE SongVersionEntity SET notes = ? WHERE id = ? AND ownerId = ?""", 3) {
          bindString(0, notes)
          bindString(1, id)
          bindString(2, ownerId)
        }.await()
    notifyQueries(-1_188_101_318) { emit ->
      emit("SongVersionEntity")
    }
  }

  public suspend fun deleteAllVersionsForSong(songId: String, ownerId: String) {
    driver.execute(1_799_704_725,
        """DELETE FROM SongVersionEntity WHERE songId = ? AND ownerId = ?""", 2) {
          bindString(0, songId)
          bindString(1, ownerId)
        }.await()
    notifyQueries(1_799_704_725) { emit ->
      emit("SongVersionEntity")
    }
  }

  public suspend fun deleteOldVersions(
    songId: String,
    ownerId: String,
    songId_: String,
    ownerId_: String,
    `value`: Long,
  ) {
    driver.execute(-1_607_360_721, """
        |DELETE FROM SongVersionEntity 
        |WHERE songId = ? AND ownerId = ?
        |AND type = 'CHECKPOINT'
        |AND id NOT IN (
        |    SELECT id FROM SongVersionEntity 
        |    WHERE songId = ? AND ownerId = ? AND type = 'CHECKPOINT'
        |    ORDER BY timestamp DESC 
        |    LIMIT ?
        |)
        """.trimMargin(), 5) {
          bindString(0, songId)
          bindString(1, ownerId)
          bindString(2, songId_)
          bindString(3, ownerId_)
          bindLong(4, value)
        }.await()
    notifyQueries(-1_607_360_721) { emit ->
      emit("SongVersionEntity")
    }
  }

  public suspend fun insertHarmonyChatMessage(
    id: String,
    songId: String,
    userId: String,
    timestamp: Long,
    text: String,
    isUser: Long,
  ) {
    driver.execute(-472_895_414, """
        |INSERT OR REPLACE INTO HarmonyChatMessageEntity(id, songId, userId, timestamp, text, isUser)
        |VALUES (?, ?, ?, ?, ?, ?)
        """.trimMargin(), 6) {
          bindString(0, id)
          bindString(1, songId)
          bindString(2, userId)
          bindLong(3, timestamp)
          bindString(4, text)
          bindLong(5, isUser)
        }.await()
    notifyQueries(-472_895_414) { emit ->
      emit("HarmonyChatMessageEntity")
    }
  }

  public suspend fun clearHarmonyChatHistory(songId: String, userId: String) {
    driver.execute(1_241_854_043,
        """DELETE FROM HarmonyChatMessageEntity WHERE songId = ? AND userId = ?""", 2) {
          bindString(0, songId)
          bindString(1, userId)
        }.await()
    notifyQueries(1_241_854_043) { emit ->
      emit("HarmonyChatMessageEntity")
    }
  }

  private inner class GetAllSongsInternalQuery<out T : Any>(
    public val ownerId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("SongEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("SongEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(1_268_782_120, """
    |SELECT * FROM SongEntity 
    |WHERE ownerId = ? OR ownerId = ''
    """.trimMargin(), mapper, 1) {
      bindString(0, ownerId)
    }

    override fun toString(): String = "SongQueries.sq:getAllSongsInternal"
  }

  private inner class GetSongByIdQuery<out T : Any>(
    public val id: String,
    public val ownerId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("SongEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("SongEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_739_295_243, """
    |SELECT * FROM SongEntity 
    |WHERE id = ? AND (ownerId = ? OR ownerId = '')
    """.trimMargin(), mapper, 2) {
      bindString(0, id)
      bindString(1, ownerId)
    }

    override fun toString(): String = "SongQueries.sq:getSongById"
  }

  private inner class GetVersionsForSongQuery<out T : Any>(
    public val songId: String,
    public val ownerId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("SongVersionEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("SongVersionEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_454_337_227, """
    |SELECT * FROM SongVersionEntity 
    |WHERE songId = ? AND (ownerId = ? OR ownerId = '')
    |ORDER BY timestamp DESC
    """.trimMargin(), mapper, 2) {
      bindString(0, songId)
      bindString(1, ownerId)
    }

    override fun toString(): String = "SongQueries.sq:getVersionsForSong"
  }

  private inner class GetHarmonyChatHistoryQuery<out T : Any>(
    public val songId: String,
    public val userId: String,
    public val `value`: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("HarmonyChatMessageEntity", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("HarmonyChatMessageEntity", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-701_081_820, """
    |SELECT * FROM HarmonyChatMessageEntity
    |WHERE songId = ? AND userId = ?
    |ORDER BY timestamp DESC
    |LIMIT ?
    """.trimMargin(), mapper, 3) {
      bindString(0, songId)
      bindString(1, userId)
      bindLong(2, value)
    }

    override fun toString(): String = "SongQueries.sq:getHarmonyChatHistory"
  }
}
